import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { Confirm, Message, Loader } from 'semantic-ui-react';
import * as Yup from 'yup';

import { FlexContainer, FlexChildren } from '../../components/Dashboard/styles';
import { Container } from './styles';
import Filter from './Filter';
import Table from '../../components/Table';
import Modal from './Modal';

import { GET_BLACKLIST } from '../../services/graphql/queries';
import { DELETE_PLATES_BLACKLIST } from '../../services/graphql/mutations';

const plateType = [
  { key: 'all', value: '', text: 'Todos' },
  { key: 'invalid', value: 'invalid', text: 'Inválido' },
  { key: 'valid', value: 'valid', text: 'Válido' }
];

const columns = [
  { id: 'mall_name', label: 'Centro Comercial', minWidth: 100 },
  { id: 'license_plate', label: 'Patente', minWidth: 50 },
  { id: 'motive', label: 'Motivo', minWidth: 100 },
  { id: 'description', label: 'Observaciones', minWidth: 100 },
  { id: 'country_name', label: 'País', minWidth: 80 },
  { id: 'date_ini', label: 'Inicio de Sospecha', minWidth: 100 },
  { id: 'date_end', label: 'Término de Sospecha', minWidth: 100 },
  { id: 'actions', label: 'Acciones', minWidth: 20 }
];

const schema = Yup.object().shape({
  begin_datetime: Yup.date().required()
});

export default function DashShowBlackList() {
  const [open, setOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [recordId, setRecordId] = useState(null);
  const [edit, setEdit] = useState(false);
  const [selected, setSelected] = useState({});
  const [motivesOptions, setMotivesOptions] = useState([]);
  const [mallsOptions, setMallsOptions] = useState([]);
  const [
    { begin_datetimeError },
    setDatesError
  ] = useState({
    begin_datetimeError: false
  });
  const [filters, setFilters] = useState({
    begin_datetime: new Date()
  });

  const { loading, error, data, refetch } = useQuery(GET_BLACKLIST, {
    variables: {
      begin_datetime: filters.begin_datetime,
      plate: filters.plate,
      mall_id: filters.mall_id,
      motives_id: filters.motives_id,
      active: true
    }
  });

  const [deleteBlacklist, { loading: deleteLoading }] = useMutation(
    DELETE_PLATES_BLACKLIST
  );

  useEffect(() => {
    if (data && data.getMalls) {
      const malls = data.getMalls.map(mall => ({
        key: mall.id,
        text: `${mall.name} - ${mall.code}`,
        value: mall.id,
        country_id: mall.country_id
      })).filter((data, index) => index !== 0);

      setMallsOptions(malls);
    }

    if (data && data.getMotives) {
      const motives = data.getMotives.map(motive => ({
        key: motive.id,
        text: motive.motive,
        value: motive.id
      }));

      setMotivesOptions(motives);
    }
  }, [data, filters]);

  async function handleFilterOnClick(
    plate,
    type,
    mall_id,
    begin_datetime,
    motives_id
  ) {
    try {
      setDatesError({
        begin_datetimeError: false
      });

      await schema.validate(
        { begin_datetime },
        { abortEarly: false }
      );

      setFilters({
        plate,
        type,
        mall_id,
        begin_datetime,
        motives_id
      });
    } catch (err) {
      err.inner &&
        err.inner.map(error =>
          setDatesError(prevState => ({
            ...prevState,
            [`${error.path}Error`]: true
          }))
        );
    }
  }

  function handleEdit(data) {
    setEdit(true);
    setSelected(data);
    setOpen(true);
  }

  function handleAdd() {
    setEdit(false);
    setOpen(true);
  }

  function handleOnDeleteClick(id) {
    setRecordId(id);
    setConfirmationOpen(true);
  }

  function handleDelete() {
    deleteBlacklist({ variables: { id: recordId } });
    setConfirmationOpen(false);
    refetch();
  }

  function handleModalClose() {
    refetch();
    setOpen(false);
  }

  return (
    <Container>
      <Confirm
        open={confirmationOpen}
        onCancel={() => setConfirmationOpen(false)}
        onConfirm={handleDelete}
        cancelButton="No"
        confirmButton="Si"
        header="Eliminar registro"
        content="¿Estás seguro de que deseas eliminar este registro?"
        data-testid="confirm-delete"
      />
      <FlexContainer>
        <FlexChildren>
          <Filter
            onClick={handleFilterOnClick}
            plateType={plateType}
            handleAdd={handleAdd}
            malls={mallsOptions}
            motives={motivesOptions}
            errors={{ begin_datetimeError }}
          />
          {(loading || deleteLoading) && (
            <>
              <br />
              <Loader active inline="centered" />
            </>
          )}
          {error && <Message error header="Error" content={error.message} />}
          {!loading && !error && data && (
            <Table
              data={data ? data.getBlacklist : null}
              columns={columns}
              handleEdit={handleEdit}
              handleDelete={handleOnDeleteClick}
            />
          )}
          <Modal
            open={open}
            onClose={handleModalClose}
            edit={edit}
            selected={selected}
            malls={mallsOptions}
            motives={motivesOptions}
          />
        </FlexChildren>
      </FlexContainer>
    </Container>
  );
}
